import type { ComponentType, SVGAttributes } from "react";

import pluralize from "pluralize";

import {
  AiSummaryIcon,
  AudienceIcon,
  AudienceTemplateIcon,
  CampaignsIcon,
  ColumnIcon,
  CubeIcon,
  DestinationIcon,
  DynamicTableIcon,
  FilterIcon,
  FlagIcon,
  FoldersIcon,
  IdentityIcon,
  JourneyIcon,
  LinkIcon,
  MailSendIcon,
  MessagesIcon,
  ModelIcon,
  SendMessageIcon,
  SourceIcon,
  SyncIcon,
  SyncTemplateIcon,
  TraitIcon,
  TraitTemplateIcon,
  VariableIcon,
  type IconBoxColors,
} from "@hightouch/htui";
import type { ResourceType } from "@hightouch/lib/lineage/graph/extractors/identity";

export type ResourceDisplay = {
  label: string;
  pluralLabel: string;
  icon: ComponentType<SVGAttributes<SVGElement>>;
  color: IconBoxColors;
};

// Used for the stacked usage bar
export function barColor(color: IconBoxColors): string {
  return `${color}.base`;
}

const RESOURCE_DISPLAY: Record<ResourceType, ResourceDisplay> = {
  model: {
    label: "Model",
    pluralLabel: "Models",
    icon: ModelIcon,
    color: "indigo",
  },
  model_column: {
    label: "Column",
    pluralLabel: "Columns",
    icon: ColumnIcon,
    color: "teal",
  },
  // TODO(ajhenry): This doesn't have an icon right now
  model_relationship: {
    label: "Relationship",
    pluralLabel: "Relationships",
    icon: LinkIcon,
    color: "teal",
  },
  audience: {
    label: "Audience",
    pluralLabel: "Audiences",
    icon: AudienceIcon,
    color: "blue",
  },
  audience_template: {
    label: "Audience template",
    pluralLabel: "Audience templates",
    icon: AudienceTemplateIcon,
    color: "blue",
  },
  trait: {
    label: "Trait",
    pluralLabel: "Traits",
    icon: TraitIcon,
    color: "orange",
  },
  trait_template: {
    label: "Trait template",
    pluralLabel: "Trait templates",
    icon: TraitTemplateIcon,
    color: "orange",
  },
  sync: {
    label: "Sync",
    pluralLabel: "Syncs",
    icon: SyncIcon,
    color: "gray",
  },
  sync_template: {
    label: "Sync template",
    pluralLabel: "Sync templates",
    icon: SyncTemplateIcon,
    color: "gray",
  },
  destination: {
    label: "Destination",
    pluralLabel: "Destinations",
    icon: DestinationIcon,
    color: "green",
  },
  // TODO(ajhenry): This doesn't have an icon right now
  destination_rule: {
    label: "Destination rule",
    pluralLabel: "Destination rules",
    icon: DestinationIcon,
    color: "green",
  },
  journey: {
    label: "Journey",
    pluralLabel: "Journeys",
    icon: JourneyIcon,
    color: "pink",
  },
  journey_context_variable: {
    label: "Journey context variable",
    pluralLabel: "Journey context variables",
    icon: VariableIcon,
    color: "orange",
  },
  // TODO(ajhenry): This doesn't have an icon right now
  subset: {
    label: "Subset",
    pluralLabel: "Subsets",
    icon: FilterIcon,
    color: "orange",
  },
  subset_category: {
    label: "Subset category",
    pluralLabel: "Subset categories",
    icon: FoldersIcon,
    color: "orange",
  },
  campaign: {
    label: "Campaign",
    pluralLabel: "Campaigns",
    icon: CampaignsIcon,
    color: "pink",
  },
  message: {
    label: "Message",
    pluralLabel: "Messages",
    icon: MessagesIcon,
    color: "pink",
  },
  idr_graph: {
    label: "Identity graph",
    pluralLabel: "Identity graphs",
    icon: IdentityIcon,
    color: "teal",
  },
  // TODO(ajhenry): This doesn't have an icon right now
  event_source: {
    label: "Event source",
    pluralLabel: "Event sources",
    icon: SourceIcon,
    color: "orange",
  },
  decision_engine_flow: {
    label: "AI Decisioning agent",
    pluralLabel: "AI Decisioning agents",
    icon: AiSummaryIcon,
    color: "yellow",
  },
  decision_engine_feature_matrix: {
    label: "Feature matrix",
    pluralLabel: "Feature matrices",
    icon: DynamicTableIcon,
    color: "yellow",
  },
  decision_engine_outcome: {
    label: "AI Decisioning outcome",
    pluralLabel: "AI Decisioning outcomes",
    icon: FlagIcon,
    color: "yellow",
  },
  decision_engine_send_event: {
    label: "AI Decisioning send event",
    pluralLabel: "AI Decisioning send events",
    icon: MailSendIcon,
    color: "yellow",
  },
  decision_engine_channel: {
    label: "AI Decisioning channel",
    pluralLabel: "AI Decisioning channels",
    icon: SendMessageIcon,
    color: "yellow",
  },
};

// In the rare cases where a resource is emitted but not known to the app (i.e. deploy skew)
// then we show this as a fallback.
function unknownResourceDisplay(resourceType: string): ResourceDisplay {
  const characters = [...resourceType.replaceAll("_", " ")];
  const label = [
    characters[0]?.toUpperCase() ?? "",
    ...characters.slice(1),
  ].join("");
  return {
    label,
    pluralLabel: pluralize(label),
    icon: CubeIcon,
    color: "gray",
  };
}

export function getDisplayNameForResource(
  resourceType: string,
): ResourceDisplay {
  return (
    RESOURCE_DISPLAY[resourceType as ResourceType] ??
    unknownResourceDisplay(resourceType)
  );
}
